import React from "react";
import {
    Box,
    Container,
    Flex,
    Heading,
    Text,
    chakra,
    VStack, HStack,
} from "@chakra-ui/react";
import FadeInSection from "../components/animation/FadeInSection";
import Highlight from "../components/Highlight";
import CTA from "../components/CTA";
import SectionCard from "../components/SectionCard";

const LandingPage = () => {
    return (
        <Box bg="gray.50">
            {/* Hero Section with Gradient Transition */}
            <Box position="relative" width="100%" overflow="hidden">
                <chakra.img
                    src="./Hero_New.png"
                    alt="Computer Assisted Animal Training"
                    width="100%"
                    objectFit="cover"
                />
                <Box
                    position="absolute"
                    bottom="0"
                    left="0"
                    width="100%"
                    height="100px"
                    bgGradient="linear(to-t, gray.50, transparent)"
                />
            </Box>
            {/* Visual separation between Hero and first card */}
            <Box h="40px"/>

            {/* Section Cards */}
            <SectionCard flexDirection="row" delay={0}>
                {/* Image left, text right */}
                <Box flex="1" textAlign="center">
                    <chakra.img
                        src="./CAAT_Logo_large.png"
                        alt="Computer Assisted Animal Training Logo"
                        maxW="350px"
                        mx="auto"
                    />
                </Box>
                <VStack flex="2" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        What is Computer Assisted Animal Training?
                    </Heading>
                    <Text fontSize="md">
                        Animal training is both an art and a science — and with the right tools, we can make it even
                        more effective, consistent, and transparent.
                    </Text>
                    <Text fontSize="md">
                        <Highlight>Computer Assisted Animal Training (CAAT)</Highlight> is all about using modern
                        technology to support trainers without replacing them. From AI-generated training plans to
                        wearable sensors and smart tools in the field, CAAT helps structure sessions, track progress,
                        and reveal patterns that are easy to miss with the naked eye.
                    </Text>
                    <Text fontSize="md">
                        But this isn’t about automation. It’s about giving trainers better information, less paperwork,
                        and more time to focus on the dog. Whether you're working alone or in a team, CAAT brings
                        clarity, efficiency, and objectivity into the process — without getting in the way.
                    </Text>
                    <Text fontSize="md">
                        <Highlight>It’s training, evolved.</Highlight>
                        <br/>
                        <Highlight>And built for the real world.</Highlight>
                    </Text>
                    {/* CTA Row */}
                    {/* Button Row */}
                    <Flex w="100%" justify="space-between" pt={2} flexWrap="wrap" gap={4}>
                        <CTA to="/concept">Learn more about CAAT</CTA>
                        <CTA to="/tech" variant="secondary">See the tech behind CAAT</CTA>
                    </Flex>
                </VStack>
            </SectionCard>

            <SectionCard flexDirection="row-reverse" delay={0.1}>
                {/* Text left, image right */}
                <Box flex="1" textAlign="center">
                    <chakra.img
                        src="./PlanTrainDoc_Hero.png"
                        alt="PlanTrainDoc Hero"
                        borderRadius="md"
                        maxW="100%"
                        mx="auto"
                    />
                    <Text fontSize="sm" color="gray.600" textAlign="center" mt={2}>
                        Stand-alone web and mobile app.
                    </Text>
                </Box>
                <VStack flex="2" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        From Planning to Progress — All in One App
                    </Heading>
                    <Text fontSize="md">
                        Training animals isn’t just about practice — it’s about planning sessions with purpose, tracking
                        what works, and adapting over time.
                    </Text>
                    <Text fontSize="md">
                        <Highlight>PlanTrainDoc</Highlight> supports the full training cycle: use the web interface to
                        create structured plans with clear criteria, then bring them into the field with the mobile app.
                        While training, you can log sessions in real time — even offline — and your data syncs
                        automatically once you’re back online.
                    </Text>
                    <Text fontSize="md">
                        Whether you're working solo or in a team, it keeps your training organized, measurable, and
                        always up to date.
                    </Text>
                    <CTA to="/plantraindoc">See what PlanTrainDoc can do</CTA>
                </VStack>
            </SectionCard>

            <SectionCard flexDirection="row" delay={0.2}>
                <Box flex="1" textAlign="center">
                    <chakra.img
                        src="./CAAT_CV_Logo.png"
                        alt="Computer Vision Logo"
                        maxW="350px"
                        mx="auto"
                    />
                </Box>
                <VStack flex="2" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        Computer Vision — Turning Training Video Into Training Data
                    </Heading>

                    <Text fontSize="md">
                        Trainers already read dogs visually: posture, movement, orientation, hesitation,
                        focus, and interaction with the environment. Computer vision starts from the same
                        source of information — but makes selected observations measurable and reusable.
                    </Text>

                    <Text fontSize="md">
                        With <Highlight>computer vision</Highlight>, ordinary training video can become more
                        than a record of what happened. It can support automatic documentation, structured
                        review, comparison across sessions, and analysis that connects training events to
                        progress over time.
                    </Text>

                    <Text fontSize="md">
                        From real-time movement analysis to cue timing, response duration, focus, and
                        handler–dog interaction, video-based systems can help reveal patterns that are
                        difficult to capture manually.
                    </Text>

                    <Text fontSize="md">
                        The goal is not to replace the trainer’s judgement. It is to help turn visual
                        information into <Highlight>training data</Highlight> — while the trainer remains focused
                        on the dog and the training process.
                    </Text>

                    <CTA to="/computer-vision">
                        Explore computer vision for animal training
                    </CTA>
                </VStack>
            </SectionCard>

            <SectionCard flexDirection="row-reverse" delay={0.2}>
                <Box flex="1" textAlign="center">
                    <chakra.img
                        src="./ai_agents.png"
                        alt="AI Agents Project"
                        borderRadius="md"
                        maxW="100%"
                        mx="auto"
                    />
                    <Text fontSize="sm" color="gray.600" textAlign="center" mt={2}>
                        Modular AI agent system – with its own web interface
                    </Text>
                </Box>
                <VStack flex="2" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        Training Plans — Written by AI, Tailored to Your Dog
                    </Heading>
                    <Text fontSize="md">
                        Structured training plans improve both quality and efficiency — but writing them is often
                        repetitive and time-consuming, especially when teams follow their own SOPs. Generic tools like
                        ChatGPT usually miss the mark: their plans are too vague and lack the structure needed for
                        real-world application.
                    </Text>
                    <Text fontSize="md">
                        That’s why we built a <Highlight>modular AI agent system</Highlight> designed specifically for
                        dog training. Each agent handles a well-defined task — from retrieving knowledge to structuring
                        and validating plans. Together, they generate detailed, customizable training plans that
                        actually fit your needs.
                    </Text>
                    <Text fontSize="md">
                        So you can focus on what matters most: the work with the dog.
                    </Text>
                    <CTA to="/ai-agents">Discover how AI creates your training plans</CTA>
                </VStack>
            </SectionCard>

            <SectionCard flexDirection="row" delay={0.5}>
                {/* Image left, text right */}
                <Box flex="1" textAlign="center">
                    <chakra.img
                        src="./CAAT_Tools_Logo.png"
                        alt="CAAT Tools Logo"
                        maxW="300px"
                        mx="auto"
                    />
                </Box>
                <VStack flex="2" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        Tools — Practical Support for Smarter Training
                    </Heading>
                    <Text fontSize="md">
                        Effective training depends on good observation, precise timing, and reliable documentation.
                        CAAT tools are built to support exactly that — helping trainers collect better information
                        while staying focused on the dog.
                    </Text>
                    <Text fontSize="md">
                        <Highlight>Sensors</Highlight> bring objectivity into the field by tracking movement, position,
                        engagement, and physiological signals that are difficult to judge by eye alone. They help reveal
                        patterns, compare sessions, and evaluate progress more reliably.
                    </Text>
                    <CTA to="/sensors">Explore sensor-based insights</CTA>

                    <Text fontSize="md">
                        <Highlight>Training aids</Highlight> simplify the practical side of a session — from
                        Bluetooth-enabled clickers that log trials automatically to devices that reduce multitasking
                        and keep the trainer’s attention where it belongs.
                    </Text>
                    <CTA to="/training_aids">Discover training aids</CTA>
                    <Text fontSize="md">
                        Together, these tools extend the trainer’s abilities without taking over the training.
                        They make sessions easier to document, easier to review, and easier to improve over time.
                    </Text>
                </VStack>
            </SectionCard>

            {/*
            <SectionCard flexDirection="row-reverse" delay={0.3}>
                <Box flex="1" textAlign="center">
                    <chakra.img
                        src="./CAAT_Sensors_Logo.png"
                        alt="Sensors Logo"
                        maxW="300px"
                        mx="auto"
                    />
                </Box>
                <VStack flex="2" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        Sensor-Based Insights — See More Than Meets the Eye
                    </Heading>
                    <Text fontSize="md">
                        Modern dog training doesn’t rely on guesswork. With the help of sensors, we bring objectivity
                        and precision into the field — tracking movement, position, engagement, and even emotional
                        state.
                    </Text>
                    <Text fontSize="md">
                        Whether it's real-time pose estimation streamed through the browser, ultra-wideband tracking
                        without GPS, sniff detection via wearables, or AI-driven analysis of body language — each
                        solution is built to provide data you can act on.
                    </Text>
                    <Text fontSize="md">
                        These tools don't replace the trainer's eye — they extend it. With recorded data, you can
                        evaluate sessions, track development over time, and compare different approaches more reliably
                        than ever before.
                    </Text>
                    <CTA to="/sensors">Explore how sensors add insight to your training</CTA>
                </VStack>
            </SectionCard>
            */}

            {/*
            <SectionCard flexDirection="row" delay={0.4}>
                <Box flex="1" textAlign="center">
                    <chakra.img
                        src="./CAAT_TrainingAids_Logo.png"
                        alt="Training Aids Logo"
                        maxW="300px"
                        mx="auto"
                    />
                </Box>
                <VStack flex="2" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        Training Aids — Tools That Let You Focus on the Dog
                    </Heading>
                    <Text fontSize="md">
                        The best training happens when the trainer is fully present. That’s why we build aids that
                        simplify and streamline your workflow — allowing you to stay focused on timing, behavior, and
                        the dog in front of you.
                    </Text>
                    <Text fontSize="md">
                        From Bluetooth-enabled clickers that log trials automatically to treat dispensers controlled
                        from your training app, these tools minimize distractions and maximize efficiency.
                    </Text>
                    <Text fontSize="md">
                        Each device is designed to integrate seamlessly into your session — giving you more freedom to
                        train, less need to multitask, and reliable documentation without breaking flow.
                    </Text>
                    <CTA to="/training_aids">Discover how training aids simplify your sessions</CTA>
                </VStack>
            </SectionCard>
            */}
        </Box>
    );
};

export default LandingPage;
